import type { CheckListItemKinds, ObservationObjectKinds, ObservationParentKinds } from './observation-types';

const DISTRIBUTION_BOARD_OBJECT_KINDS = [
    'DistributionBoard',
    'DistributionBoardFile',
    'DistributionBoardDevice',
    'DistributionBoardInformation',
    'DistributionBoardMeasurements',
    'DistributionBoardImages',
    'DistributionBoardThermalImages',
    'DistributionBoardGeneralInformation',
    'DistributionBoardCurrent',
    'DistributionBoardVoltage',
    'DistributionBoardCircuitImpedance',
    'DistributionBoardNetImpedance',
    'RisoMeasurement',
] as const satisfies readonly ObservationObjectKinds[];

const FINAL_CIRCUIT_OBJECT_KINDS = [
    'FinalCircuit',
] as const satisfies readonly ObservationObjectKinds[];

const PV_INVERTER_OBJECT_KINDS = [
    'PVInverter',
    'PVString',
] as const satisfies readonly ObservationObjectKinds[];

const PV_INVERTER_CHECKLIST_KINDS = [
    'Scope12VisualInspection',
    'Scope12AdditionalInspectionPoints',
    'Scope12DocumentationDCSide',
    'Scope12ManualAndDataSpecifications',
    'Scope12GeneralRemarks',
    'Scope12PVInstallationPowerLine',
    'Scope12InspectionExecution',
    'Scope12DocumentationACSide',
] as const satisfies readonly CheckListItemKinds[];

export type GetObservationParentKindProps<ObjectKind extends ObservationObjectKinds> = ObjectKind extends 'CheckListItem'
    ? {
            objectKind: ObjectKind; // "CheckListItem" | "DistributionBoard" | "FinalCircuit" | "DistributionBoardFile" | "DistributionBoardDevice" | "RisoMeasurement" | "PVInverter" | "PVString" | "DistributionBoardInformation" | "DistributionBoardMeasurements" | "DistributionBoardImages" | "DistributionBoardThermalImages" | "DistributionBoardGeneralInformation" | "DistributionBoardCurrent" | "DistributionBoardVoltage" | "DistributionBoardCircuitImpedance" | "DistributionBoardNetImpedance"
            checkListItemKind: CheckListItemKinds; // "CheckListItem" | "DistributionBoard" | "FinalCircuit" | "DistributionBoardFile" | "DistributionBoardDevice" | "RisoMeasurement" | "PVInverter" | "PVString" | "DistributionBoardInformation" | "DistributionBoardMeasurements" | "DistributionBoardImages" | "DistributionBoardThermalImages" | "DistributionBoardGeneralInformation" | "DistributionBoardCurrent" | "DistributionBoardVoltage" | "DistributionBoardCircuitImpedance" | "DistributionBoardNetImpedance"
        }
    : {
            objectKind: ObjectKind; // "DistributionBoard" | "FinalCircuit" | "DistributionBoardFile" | "DistributionBoardDevice" | "CheckListItem" | "RisoMeasurement" | "PVInverter" | "PVString" | "DistributionBoardInformation" | "DistributionBoardMeasurements" | "DistributionBoardImages" | "DistributionBoardThermalImages" | "DistributionBoardGeneralInformation" | "DistributionBoardCurrent" | "DistributionBoardVoltage" | "DistributionBoardCircuitImpedance" | "DistributionBoardNetImpedance"
        };

// Type guard to check if props has checkListItemKind
function hasCheckListItemKind(
    props: GetObservationParentKindProps<ObservationObjectKinds>,
): props is GetObservationParentKindProps<'CheckListItem'> {
    return props.objectKind === 'CheckListItem';
}

// Helper to check if a value is in a readonly array
function isInArray<T>(
    value: T,
    array: readonly T[],
): value is T {
    return array.includes(value);
}

export function getObservationParentKind<ObjectKind extends ObservationObjectKinds>(
    props: GetObservationParentKindProps<ObjectKind>,
): ObservationParentKinds {
    // Handle CheckListItem based on parentKind
    if (hasCheckListItemKind(props)) {
        const { checkListItemKind } = props;

        // If parent is distribution board related
        if (isInArray(checkListItemKind, DISTRIBUTION_BOARD_OBJECT_KINDS)) {
            return 'DistributionBoard';
        }
        // If parent is final circuit
        if (isInArray(checkListItemKind, FINAL_CIRCUIT_OBJECT_KINDS)) {
            return 'FinalCircuit';
        }
        // If parent is PV inverter related
        if (isInArray(checkListItemKind, PV_INVERTER_CHECKLIST_KINDS)) {
            return 'PVInverter';
        }

        // Default fallback
        return 'DistributionBoard';
    }

    const { objectKind } = props;

    // Handle other kinds based on their group
    if (isInArray(objectKind, DISTRIBUTION_BOARD_OBJECT_KINDS)) {
        return 'DistributionBoard';
    }

    if (isInArray(objectKind, FINAL_CIRCUIT_OBJECT_KINDS)) {
        return 'FinalCircuit';
    }

    if (isInArray(objectKind, PV_INVERTER_OBJECT_KINDS)) {
        return 'PVInverter';
    }

    // Default fallback
    return 'DistributionBoard';
}
